import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Button, Dialog, DialogContent, Grid, IconButton, Stack, Tooltip } from "@mui/material";
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { useStatePtr } from '@pure-ptr/react';
import Helmet from 'react-helmet';
import { injectIntl } from "react-intl";
import { useDispatch,useSelector } from 'react-redux';
import { useData } from "@pure-ptr/react";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useFitHeight } from '../../../../_metronic/layout/Layout';
import { TableLevelAction } from '../../../common';
import { Announcement } from '../../../common/Announcement';
import { pageStore, useGlobalStore, XDataGrid } from '../../../common';
import {
    Portlet, PortletBody,
    PortletHeader
} from "../../../partials/content/Portlet";
import { get } from '../../../store/actions/brand';
import { download, setChargeMasterEditDialog, uploadChargeMaster } from '../../../store/actions/chargeMaster';
import { chargeMasterLanding, resetPage, set } from '../../../store/actions/pages';
import { integerEquators, StringEquators } from '../../../store/constants/pages';
import { createCurrencyDataField, createStringDataField, createStringFieldFromMultiKeys } from '../../../util/format';
import { errorToastify, infoToastify } from '../../../util/toast';
import { DeleteConfirmation } from '../../../common/GenericDeleteConfirmationDialog/DeleteConfirmationDialog';
import { ChargeMasterDialog } from './ChargeMasterDialog';
import { getChargesDependencies ,deleteCharges} from '../../../store/api/chargeMaster';
import { DependentsList } from "../../../common";
import { RootState } from '../../../store/reducers';
import { selectSessionHasRcmAssignmentAccess as selectSessionHasChargeMasterDeleteAccess } from '../../../store/selectors/session';

const DEFAULT_SORT = [{ id: 'code', direction: 'asc' }];
const DEFAULT_HIDDEN_FIELDS = ['drugTypeOfMeasurement', 'drugUnitOfMeasurement', 'ndc'];    

const { usePageStore, useGridFilters } = pageStore('charge-master-landing', {
    sort: [{ id: 'code', direction: 'asc' }]
})

function ChargeMasterPage({ history, intl }) {
    useFitHeight();

    const dispatch = useDispatch();
    let state: any = useSelector<RootState>(
            state => state
        );
    const hasDeleteAccess=selectSessionHasChargeMasterDeleteAccess(state)

    const chargeMasterData = usePageStore( s => {
        return s.list;
    });
    const chargeMasterUploadPending = useGlobalStore( state => state.chargeMaster.upload.pending);
    const chargeMasterDownloadTemplatePending = useGlobalStore(state => state.chargeMaster.download.pending);
    const pending = usePageStore( s => s.pending);

    const brandId = useGlobalStore( s => s.brand.data?.brandId );

    const [refresh, setRefresh] = useState(Promise.resolve());

    const selectedIdsPtr = useStatePtr<number[]>([]);
    const deleteDialogPtr: any = useStatePtr(() => ({
            rowData: null,
            open: false,
            confirmDelete:false,
            confirmationOnly:false
    }));

    useEffect(() => {
        const pageName = { name: 'charge-master-landing' };
        dispatch(set(pageName.name, 'waitForCriteriaFilter', false));
        return () => {
            dispatch(resetPage(pageName.name));
        }
    }, [dispatch]);

    useEffect(() => {
        dispatch(get());
    }, [dispatch]);

    function handleRefresh() {
        dispatch(set('charge-master-landing', 'list', []));
        setRefresh(Promise.resolve());
    }



    const {
        data,
        isPending: isPendingDelete,
        error: deleteError,
    } = useData(async () => {
        if (!deleteDialogPtr?.value?.confirmDelete) return;

        const deleteResponse = await deleteCharges({
            chargeMasterIds:
                deleteDialogPtr?.value?.rowData || selectedIdsPtr?.value,
        });
        deleteDialogPtr.set({
            ...deleteDialogPtr.value,
            rowData: null,
            confirmDelete: true,
            confirmationOnly:true
        });
        handleRefresh();
        return deleteResponse;
    }, [deleteDialogPtr?.value?.confirmDelete]);

    const { data: chargeDependencies, isPending: isPendingUpdate } =
        useData(async () => {
            if (!deleteDialogPtr?.value?.open) return;

            const dependenciesResponse = await getChargesDependencies({
                chargeMasterIds:
                    deleteDialogPtr?.value?.rowData || selectedIdsPtr?.value,
            });
            return dependenciesResponse;
        }, [deleteDialogPtr?.value?.open]);

    useEffect(() => {
        if (!deleteError) return;
        errorToastify(deleteError?.payload?.error);
    }, [deleteError]);
    

    
    return (
        <>
            <Announcement
                text="This page (Charge Master) is a dependency for both Pricer and Estimator products"
                backgroudColor={undefined}
            />
            <Helmet title="Charge Master" />
            <Portlet>
                <PortletHeader
                    title={"Charge Master"}
                    name="charge-master-landing"
                    showSearchFilters={true}
                    showSearchFiltersDropdown={false}
                    handleRefresh={handleRefresh}
                />
                <PortletBody>
                    <Stack
                        direction="column"
                        spacing={1}
                        sx={{ height: "100%" }}
                    >
                        <GridLevelActions
                            hasDeleteAccess={hasDeleteAccess}
                            deleteDialogPtr={deleteDialogPtr}
                            handleRefresh={handleRefresh}
                            brandId={brandId}
                            dispatch={dispatch}
                            loading={
                                chargeMasterDownloadTemplatePending ||
                                chargeMasterUploadPending
                            }
                        />

                        <ChargeMasterDataGrid
                            hasDeleteAccess={hasDeleteAccess}

                            loading={
                                chargeMasterDownloadTemplatePending ||
                                chargeMasterUploadPending ||
                                pending
                            }
                            data={chargeMasterData}
                            deleteDialogPtr={deleteDialogPtr}
                            refresh={refresh}
                            dispatch={dispatch}
                            selectedIdsPtr={selectedIdsPtr}
                        />
                        <DeleteConfirmation
                            doubleConfirm={true}
                            customCancelBtnText={deleteDialogPtr.value?.confirmationOnly && "OK"}
                            defaultSelected={selectedIdsPtr?.value || []}
                            isConfirmationOnly={deleteDialogPtr.value?.confirmationOnly}
                            customTitle={deleteDialogPtr.value?.confirmationOnly ?"Regenerate MRF":"Are you sure ?"}
                            handleDelete={() => deleteDialogPtr.set({
                                    ...deleteDialogPtr.value,
                                    confirmDelete:true
                                })}
                            handleClose={() =>
                                deleteDialogPtr.set({
                                    open: false,
                                    rowData: null,
                                    confirmDelete:false,
                                    confirmationOnly:false
                                })
                            }
                            preserverOriginalHTMLFormat={true}
                            isDeleting={isPendingDelete!=null}
                            DELETE_TEXT={deleteDialogPtr.value?.confirmationOnly ? "You can regenerate the MRF now.":`You are about to delete ${
                                (deleteDialogPtr?.value?.rowData || selectedIdsPtr?.value?.length === 1)
                                    ? "<b>[1 Item]</b>"
                                    : `<b>[${selectedIdsPtr?.value?.length} Items]</b>`
                            }.${
                                chargeDependencies?.records?.length > 0
                                    ? " Following Dependencies are found."
                                    : ""
                            } This change cannot be undone.`}
                            isPending={isPendingUpdate != null}
                            defaultDeleteState={deleteDialogPtr?.value}
                            canDelete={true}
                            hideDeleteBtn={deleteDialogPtr.value?.confirmationOnly}
                            NO_ITEMS_TEXT="No Charge selected, Please select at least 1 to delete"
                        >
                            {!deleteDialogPtr.value?.confirmationOnly && <DependentsList
                                dependents={chargeDependencies?.records || []}
                                isSimpleDependency={true}
                            />}
                        </DeleteConfirmation>
                    </Stack>
                </PortletBody>
            </Portlet>

            <ChargeMasterDialog handleRefresh={handleRefresh} />


        </>
    ); 
}

const GridLevelActions = ({ handleRefresh, brandId, dispatch, loading, deleteDialogPtr,hasDeleteAccess}) => {
    function downloadTemplateCallback({ file, fileName }) {
        const displayName = brandId ?? fileName;
        let url = window.URL.createObjectURL(file);
        let a = document.createElement('a');
        a.href = url;
        a.download = `${displayName.replace(/\s/g, '')}_ChargeMaster.csv`;
        a.click();
    }

    const uploadChargeMasterRef = useRef(null);

    function handleFileUpload() {
        uploadChargeMasterRef.current.click();
    }

    const [file, setFile] = useState(null);

    const handleFileChange = (event) => {
        if (event.target.files.length > 0 && event.target.files[0].type === 'text/csv') {
            setFile(event.target.files[0]);
            dispatch(uploadChargeMaster(event.target.files[0], handleFileUploadCallback, errorCallback));
        } else if (event.target.files.length > 0) {
            errorToastify('Invalid format, please make sure the file is in CSV format.');
        }
    };

    const handleFileUploadCallback = () => {
        infoToastify(<><b>Uploading Charge Master</b><br/>You'll be notified when it's complete.</>);
        setFile(null);
        handleRefresh();
        (document.getElementById('charge-master-upload-input') as HTMLInputElement).value = '';
    };

    const errorCallback = () => {
        setFile(null);
        (document.getElementById('charge-master-upload-input') as HTMLInputElement).value = '';
    };

    function handleAddCharge() {
        dispatch(setChargeMasterEditDialog({ open: true, charge: null, mode: 'create' }));
    }

    return (
        <TableLevelAction
            handleRefresh={handleRefresh}
            actions={() => (
                <>
                    {hasDeleteAccess && (
                        <Grid item>
                            <Tooltip title="Delete">
                                <IconButton
                                    aria-label="delete"
                                    size="small"
                                    onClick={() =>
                                        deleteDialogPtr.set({
                                            open: true,
                                            rowData: null,
                                            confirmDelete: false,
                                        })
                                    }
                                    disabled={loading}
                                >
                                    <DeleteIcon />{" "}
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    )}
                    <Grid item>
                        <Tooltip title="Download Template">
                            <IconButton
                                aria-label="download"
                                size="small"
                                onClick={() =>
                                    dispatch(download(downloadTemplateCallback))
                                }
                                disabled={loading}
                            >
                                <GetAppIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Tooltip title="Upload Charge Master">
                            <IconButton
                                aria-label="upload"
                                size="small"
                                onClick={handleFileUpload}
                                disabled={loading}
                            >
                                <i className="fas fa-upload"></i>
                                <input
                                    id="charge-master-upload-input"
                                    onInput={handleFileChange}
                                    ref={uploadChargeMasterRef}
                                    type="file"
                                    hidden
                                />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </>
            )}
            onAdd={handleAddCharge}
            loading={loading}
            name="Charge"
        />
    );
}

const ChargeMasterDataGrid = ({ refresh, data, loading, dispatch,selectedIdsPtr,deleteDialogPtr,hasDeleteAccess }) => {
    const fields = useMemo( () => [
        createStringDataField('facilityName', 'Facility Name', { sortable: true, equators: StringEquators }),
        createStringDataField('departmentName', 'Department Name', { sortable: true, equators: StringEquators }),
        createStringDataField('code', 'Charge Code', { sortable: true, default: true, equators: StringEquators }),
        createStringDataField('revenueCode', 'Rev Code', { sortable: true, equators: StringEquators }),
        createStringDataField('drgCode', 'DRG Code', { sortable: true, equators: StringEquators }),
        createStringDataField('service', 'Service', { sortable: true, equators: StringEquators }),
        createStringDataField('cpT_HCPCS', 'CPT/HPCS', { sortable: true, equators: StringEquators }),
        createStringDataField('description', 'Description', { sortable: true, equators: StringEquators }),
        createStringFieldFromMultiKeys(['mod1', 'mod2', 'mod3', 'mod4'], 'Modifiers', { hideFromFilter: true, returnWholeRow: true, commaSeparated: true, sortColumn: 'mod1' }),
        createStringDataField('drugTypeOfMeasurement', 'Drug Type of Measurement', { sortable: true, equators: StringEquators }),
        createStringDataField('drugUnitOfMeasurement', 'Drug Unit of Measurement', { sortable: true, equators: StringEquators }),
        createStringDataField('ndc', 'NDC', { sortable: true, equators: StringEquators }),
        createStringDataField('gl', 'GL', { sortable: true, equators: StringEquators }),
        createCurrencyDataField('amount', 'Charge', { sortable: true, equators: integerEquators }),
    ], []);

    const tableFilter = useGridFilters();

    const chargeMasterActions = useCallback( props => [
            <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={ () => 
                dispatch( 
                    setChargeMasterEditDialog({ 
                        open: true, 
                        charge: props.row, 
                        mode: 'edit' 
                    })
                )
            }
        />,
        ...(hasDeleteAccess
            ? [
                <GridActionsCellItem
                    icon={<DeleteIcon />}
                    label="Delete"
                    onClick={() =>
                        deleteDialogPtr.set({
                            open: true,
                            rowData: [props.row?.id],
                            confirmDelete: false,
                        })
                    }
                />,
            ] : []),
        ],
        []
    );

    return (
        <XDataGrid
            name="charge-master-landing"
            fields={fields}
            loading={loading}
            data={data}
            selectedIdsPtr={selectedIdsPtr}
            paginationFilters={tableFilter}
            onLoad={(sort, filter, pagination) =>
                dispatch(chargeMasterLanding(pagination, sort, filter))
            }
            refresh={refresh}
            defaultSort={DEFAULT_SORT}
            criteriasSaveEnabled={true}
            actions={ chargeMasterActions }
            showSearchFilters={true}
            defaultHiddenFields={DEFAULT_HIDDEN_FIELDS}
        />
    )
}

export default injectIntl(ChargeMasterPage);

